import { z } from 'zod';
import { SEAT_CELL_TYPES } from '../constants.js';
import { cuidSchema, hhmmSchema, isoDateSchema, paginationQuerySchema } from './common.js';
/** Request contracts for the tenant catalog (docs/10 "/admin" fleet & network). */
// --- Seat layouts -----------------------------------------------------------
export const seatCellSchema = z.object({
    code: z.string().trim().max(8),
    row: z.number().int().min(0).max(99),
    col: z.number().int().min(0).max(19),
    type: z.enum(SEAT_CELL_TYPES),
    tierId: cuidSchema.optional(),
});
export const seatLayoutInputSchema = z.object({
    name: z.string().trim().min(1).max(60),
    rows: z.number().int().min(1).max(100),
    columns: z.number().int().min(1).max(20),
    seats: z.array(seatCellSchema).min(1).max(2000),
});
// --- Buses ------------------------------------------------------------------
export const busInputSchema = z.object({
    name: z.string().trim().min(1).max(60),
    plateNumber: z.string().trim().min(1).max(20),
    seatLayoutId: cuidSchema,
    active: z.boolean().default(true),
});
// --- Stops ------------------------------------------------------------------
export const stopInputSchema = z.object({
    name: z.string().trim().min(1).max(80),
    city: z.string().trim().min(1).max(80),
    address: z.string().trim().max(200).optional(),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    active: z.boolean().default(true),
});
// --- Routes -----------------------------------------------------------------
export const routeStopInputSchema = z.object({
    stopId: cuidSchema,
    /** Minutes after departure from the first stop. */
    arrivalOffsetMin: z.number().int().min(0).max(10_080),
});
export const routeInputSchema = z.object({
    name: z.string().trim().min(1).max(80),
    active: z.boolean().default(true),
    stops: z
        .array(routeStopInputSchema)
        .min(2, 'A route needs at least an origin and a destination')
        .max(50)
        .refine((stops) => new Set(stops.map((s) => s.stopId)).size === stops.length, 'A stop can only appear once on a route')
        .refine((stops) => stops[0]?.arrivalOffsetMin === 0, 'The first stop is the departure point, so its offset must be 0')
        .refine((stops) => stops.every((stop, i) => i === 0 || stop.arrivalOffsetMin > stops[i - 1].arrivalOffsetMin), 'Arrival offsets must increase along the route'),
});
// --- Fares ------------------------------------------------------------------
export const fareInputSchema = z.object({
    fromStopId: cuidSchema,
    toStopId: cuidSchema,
    priceCents: z.number().int().min(0).max(100_000_000),
});
export const faresUpdateSchema = z.object({
    fares: z.array(fareInputSchema).max(2500),
});
// --- Timetables -------------------------------------------------------------
export const timetableInputSchema = z
    .object({
    routeId: cuidSchema,
    busId: cuidSchema,
    departureTime: hhmmSchema,
    daysOfWeek: z
        .array(z.number().int().min(0).max(6))
        .min(1, 'Choose at least one day')
        .max(7)
        .refine((days) => new Set(days).size === days.length, 'Days must be unique'),
    validFrom: isoDateSchema,
    validTo: isoDateSchema.nullable().optional(),
    active: z.boolean().default(true),
})
    .refine((input) => !input.validTo || input.validTo >= input.validFrom, { message: 'The end date cannot be before the start date', path: ['validTo'] });
// --- Trips ------------------------------------------------------------------
export const adHocTripSchema = z.object({
    routeId: cuidSchema,
    busId: cuidSchema,
    /** Local date and time in the tenant's timezone. */
    date: isoDateSchema,
    departureTime: hhmmSchema,
    note: z.string().trim().max(200).optional(),
});
export const tripListQuerySchema = paginationQuerySchema.extend({
    date: isoDateSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    routeId: cuidSchema.optional(),
    status: z.enum(['SCHEDULED', 'DELAYED', 'DEPARTED', 'CANCELLED']).optional(),
});
export const tripDelaySchema = z.object({
    minutes: z.number().int().min(0).max(1440),
    note: z.string().trim().max(200).optional(),
});
export const tripNoteSchema = z.object({
    note: z.string().trim().max(200),
});
export const tripCancelSchema = z.object({
    autoRefund: z.boolean().default(true),
    reason: z.string().trim().max(200).optional(),
});
// --- staff, settings, check-in ----------------------------------------------
export const staffInviteSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    name: z.string().trim().min(1).max(120),
    /** Which of the tenant's TenantRole rows this account is invited into (custom staff roles). */
    tenantRoleId: z.string().trim().min(1),
});
export const staffUpdateSchema = z.object({
    tenantRoleId: z.string().trim().min(1).optional(),
    active: z.boolean().optional(),
});
export const checkInSchema = z
    .object({
    qrToken: z.string().trim().min(8).max(200).optional(),
    reference: z.string().trim().min(3).max(20).optional(),
    seatCode: z.string().trim().min(1).max(8).optional(),
})
    .refine((input) => Boolean(input.qrToken) || Boolean(input.reference && input.seatCode), 'Provide a scanned code, or a booking reference with a seat');
export const bookingSearchSchema = paginationQuerySchema.extend({
    query: z.string().trim().max(120).optional(),
    status: z
        .enum(['PENDING_PAYMENT', 'CONFIRMED', 'CANCELLED', 'REFUNDED', 'EXPIRED'])
        .optional(),
    date: isoDateSchema.optional(),
});
//# sourceMappingURL=catalog.js.map